import React, { useEffect, useMemo, useState } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { FormSelect } from '../../setup/components/FormInput';
import { useEFiling } from '../contexts/EFilingContext';
import {
  getCaseCategoryFieldVisibility,
  getCourtJurisdictions,
} from '@lds-efsp/shared-contracts';
import type { CourtJurisdictionRef } from '@lds-efsp/shared-contracts';
import { useApi, API_BASE_URL } from '../../../api/useApi';
import { formatCourtJurisdictionName } from '../lib/court-display';

const API_BASE = API_BASE_URL;
const NEEDED_HINT = '⚠ Needed before submitting';

interface RefOption {
  code: string;
  name: string;
}

interface CourtJurisdictionFieldProps {
  brand: BrandConfig;
}

/**
 * Court Jurisdiction select for the consolidated "Confirm your filing" page
 * (Walter R64): a county-scoped sub-location, so it renders in the Court
 * section directly beneath County (via OrderInfoTab's courtJurisdictionSlot)
 * instead of down in the Case section where the classic step 3 shows it.
 *
 * Self-contained on purpose (cut 1): it re-fetches the case-fields visibility
 * gate + jurisdiction options that CaseInfoTab also fetches, rather than
 * restructuring CaseInfoTab's internals before phase 2. CaseInfoTab (mounted
 * below on the same page with its own select hidden) still owns the
 * single-option auto-select and the zip→courthouse auto-select; both write
 * the same caseInfo.courtJurisdiction this select renders.
 *
 * The R65 reset needs nothing here: OrderInfoTab's state/county handlers
 * already clear courtJurisdiction (WOL-341), and a category change clears it
 * in CaseInfoTab's handleCategoryChange.
 */
const CourtJurisdictionField: React.FC<CourtJurisdictionFieldProps> = ({ brand }) => {
  const { wizardState, updateCaseInfo } = useEFiling();
  const { caseInfo, orderInfo } = wizardState;
  const { fetchWithAuth } = useApi();

  const isSubsequent = orderInfo.filingType === 'subsequent-filing';

  const [showField, setShowField] = useState(false);
  const [jurisdictions, setJurisdictions] = useState<RefOption[]>([]);
  const [loading, setLoading] = useState(false);

  /* eslint-disable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- async fetch effects: synchronous reset before fetch, setState in callbacks */
  useEffect(() => {
    if (!caseInfo.caseCategoryCode || !orderInfo.county) {
      setShowField(false);
      return;
    }
    const params = new URLSearchParams({
      county: orderInfo.county,
      categoryCode: caseInfo.caseCategoryCode,
    });
    fetchWithAuth(`${API_BASE}/api/efiling/reference/case-fields?${params}`)
      .then(res => res.json())
      .then(data => setShowField(Boolean(data?.showCourtJurisdiction)))
      .catch(() => {
        setShowField(
          getCaseCategoryFieldVisibility(caseInfo.caseCategoryCode).showCourtJurisdiction,
        );
      });
  }, [caseInfo.caseCategoryCode, orderInfo.county, fetchWithAuth]);

  useEffect(() => {
    if (!showField) {
      setJurisdictions([]);
      return;
    }
    // Drop the previous county's options synchronously — a county change must
    // never leave the old county's courthouses selectable while the refetch is
    // in flight (WOL-420 / Walter R74).
    setJurisdictions([]);
    setLoading(true);
    const params = new URLSearchParams({
      county: orderInfo.county,
      categoryCode: caseInfo.caseCategoryCode,
    });
    fetchWithAuth(`${API_BASE}/api/efiling/reference/court-jurisdictions?${params}`)
      .then(res => res.json())
      .then(json => setJurisdictions(json.data || []))
      .catch(() => {
        setJurisdictions(getCourtJurisdictions(caseInfo.caseCategoryCode));
      })
      .finally(() => setLoading(false));
  }, [showField, caseInfo.caseCategoryCode, orderInfo.county, fetchWithAuth]);
  /* eslint-enable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */

  // Display labels only (Walter R73, delivered flag-gated per R80): the EFM
  // policy names are internal tokens ("Madera Courthouse"), so this
  // confirm-flow-only select renders the proper court name instead. The
  // persisted value stays the raw option (code + name) — the envelope and the
  // classic flow's shared state never see the formatted label.
  const options = useMemo(
    () => [
      { value: '', label: loading ? 'Loading...' : 'Select court jurisdiction...' },
      ...jurisdictions.map(cj => ({
        value: cj.code,
        label: formatCourtJurisdictionName(cj.name, orderInfo.county, orderInfo.state),
      })),
    ],
    [jurisdictions, loading, orderInfo.county, orderInfo.state],
  );

  // Both case-scoped guards are load-bearing AFTER a court change, and the
  // absence they produce is the intended behaviour rather than a regression of
  // R65. This control is CATEGORY-scoped — `showField` comes from
  // `/reference/case-fields?county=&categoryCode=` and the options from
  // `/reference/court-jurisdictions` keyed the same way — so once SPEC-SOR-174
  // R17 clears the case category and type below the changed court there is no
  // court/category pair to ask policy about, and rendering a select here would
  // be offering the previous court's question. It returns as soon as the filer
  // re-establishes the category under the new court, empty: R65's substance is
  // that the courthouse does not carry across, and R17 generalizes that to
  // everything below the court rather than weakening it. Pinned by the R65/R17
  // test in `e2e/specs/confirm-flow.spec.ts`.
  if (isSubsequent || !caseInfo.caseType || !showField) return null;

  const needsValue = !caseInfo.courtJurisdiction;

  return (
    <FormSelect
      brand={brand}
      label="Court Jurisdiction"
      name="courtJurisdiction"
      value={caseInfo.courtJurisdiction?.code || ''}
      onChange={e => {
        const code = e.target.value;
        const opt = jurisdictions.find(cj => cj.code === code);
        updateCaseInfo({
          courtJurisdiction: opt
            ? ({ code: opt.code, name: opt.name } as CourtJurisdictionRef)
            : null,
        });
      }}
      options={options}
      required
      validationStatus={needsValue ? 'warning' : undefined}
      validationMessage={needsValue ? NEEDED_HINT : undefined}
    />
  );
};

export default CourtJurisdictionField;
